/* eslint-disable tailwindcss/no-custom-classname */
import type { ICategoryGroup } from '@/store/organization/categoryGroupSlice';
import type { IKeywordGroup } from '@/store/organization/keywordGroupSlice';
import type { IProject } from '@/store/organization/projectSlice';
import { Marketplace } from '@/store/organization/projectSlice';
import type { RootState } from '@/store/store';
import type { FC } from 'react';

import { Dropdown, Select, ToggleSwitch } from 'flowbite-react';
// import { Dropdown, Select, ToggleSwitch, Radio, Label } from 'flowbite-react';

import { marketplacesWithAds } from '@/data/marketplaces';
import { useStateWithQuery } from '@/hooks';
import { StatusEnum } from '@/store/common-types';
import { Select as MultiSelect } from 'antd';
import { memo, useEffect, useLayoutEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import BrandsColorEditor from '../pages/dashboard-search/components/brands-color-editor';
import calculateActualKeywordsAndCategories from '../pages/dashboard-search/helper-functions/calculateActualKeywordsAndCategories';
import MarketplaceDropdown from './MarketplaceDropdown';
import DateRange from './dateRange';

export enum CurrentPageEnum {
  Dashboard = 'dashboard',
  ReportGoods = 'reportGoods',
}

type IFiltersProps = {
  setFilters: React.Dispatch<React.SetStateAction<null | IFilters>>;
  showBrands?: boolean;
  currentPage: CurrentPageEnum;
  isMyBrands?: boolean;
  setIsMyBrands?: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedProject?: React.Dispatch<
    React.SetStateAction<string | number | undefined>
  >;
};

export enum FiltersTypeEnum {
  Brandshelf = 'brandshelf',
  Ads = 'ad',
}

export enum SellerTypeEnum {
  All = 'all',
  OneSeller = '1p',
  ThirdPartySeller = '3p',
}

export type IFilters = {
  start_date: string;
  end_date: string;
  keywords: string[];
  '1P': boolean;
  platform: Marketplace;
  type?: FiltersTypeEnum;
  regions: string[];
};

type IFilterState = {
  sellerType: SellerTypeEnum;
  isBrandshelf?: boolean;
  isAds?: boolean;
  selectedClient?: string | number;
  selectedProject?: string | number;
  // dateRange: string;
  selectedMarketplace: Marketplace;
  selectedKeywordsAndCategoryGroups: string[];
  selectedKeywordsAndCategories: string[];
};

const Filters: FC<IFiltersProps> = ({
  setFilters,
  showBrands,
  currentPage,
  isMyBrands,
  setIsMyBrands,
  setSelectedProject,
}) => {
  const [searchParams] = useSearchParams();

  const clients = useSelector((state: RootState) => state.clients.clients);

  const { projects, status: projectsStatus } = useSelector(
    (state: RootState) => state.projects
  );

  const [state, setState] = useStateWithQuery<IFilterState>({
    sellerType:
      JSON.parse(searchParams.get('sellerType') as string) ||
      SellerTypeEnum.All,
    isBrandshelf:
      JSON.parse(searchParams.get('isBrandshelf') as string) || false,
    isAds: JSON.parse(searchParams.get('isAds') as string) || false,
    selectedMarketplace:
      JSON.parse(searchParams.get('selectedMarketplace') as string) ||
      Marketplace.Ozon,
    selectedKeywordsAndCategoryGroups: [
      ...new Set(
        JSON.parse(
          searchParams.get('selectedKeywordsAndCategoryGroups') as string
        ) || []
      ),
    ] as string[],
    selectedKeywordsAndCategories: [
      ...new Set(
        JSON.parse(
          searchParams.get('selectedKeywordsAndCategories') as string
        ) || []
      ),
    ] as string[],
    selectedClient:
      JSON.parse(searchParams.get('selectedClient') as string) || undefined,
    selectedProject:
      JSON.parse(searchParams.get('selectedProject') as string) || undefined,
  });

  const [dateRange, setDateRange] = useStateWithQuery<string>(
    JSON.parse(searchParams.get('dateRange') as string) || '',
    'dateRange'
  );

  const { keywordGroups, status: keywordGroupsStatus } = useSelector(
    (state: RootState) => state.keywordGroups
  );
  const { categoryGroups, status: categoryGroupsStatus } = useSelector(
    (state: RootState) => state.categoryGroups
  );

  const clearState = (nextState: Partial<IFilterState>) =>
    setState((prev) => ({
      ...prev,
      ...nextState,
      selectedProject: undefined,
      selectedKeywordsAndCategories: [],
      selectedKeywordsAndCategoryGroups: [],
    }));

  const transformKeywordToId = ({ id }: IKeywordGroup) => `kg-${id}`;
  const transformCategoryToId = ({ id }: ICategoryGroup) => `cg-${id}`;

  const actualClients = useMemo(() => {
    return clients.filter(({ id }) => {
      if (
        projects.find(
          ({ client, marketplace: { marketplace_type } }) =>
            marketplace_type === state.selectedMarketplace && +client === +id
        )
      ) {
        return true;
      }
      return false;
    });
  }, [state.selectedMarketplace, clients, projects]);

  const actualProjects = useMemo(() => {
    const { selectedClient, selectedMarketplace } = state;

    if (selectedClient !== undefined) {
      const filteredProjects = projects.filter(
        ({ client, marketplace: { marketplace_type } }) =>
          client === +selectedClient && marketplace_type === selectedMarketplace
      );
      return filteredProjects;
    }
    // setState((prev) => ({ ...prev, selectedProject: undefined }));
    return [];
  }, [
    state.selectedClient,
    state.selectedMarketplace,
    projects,
    actualClients,
  ]);

  const actualKeywordAndCategoryGroups = useMemo(() => {
    const { selectedProject } = state;

    if (selectedProject !== undefined) {
      const findProjectCallback = ({ project }: { project: number }) =>
        project === +selectedProject;

      const projectKeywordGroups = keywordGroups.filter(findProjectCallback);
      const projectCategoryGroups = categoryGroups.filter(findProjectCallback);

      return [
        ...projectKeywordGroups.map(transformKeywordToId),
        ...projectCategoryGroups.map(transformCategoryToId),
      ];
    }

    // setState((prev) => ({ ...prev, selectedKeywordsAndCategoryGroups: [] }));

    return [];
  }, [categoryGroups, keywordGroups, state.selectedProject]);

  const actualKeywordsAndCategories = useMemo(() => {
    return calculateActualKeywordsAndCategories(
      state.selectedKeywordsAndCategoryGroups,
      keywordGroups,
      categoryGroups
    );
  }, [categoryGroups, keywordGroups, state.selectedKeywordsAndCategoryGroups]);

  const getDataIdSelectedOption = (
    options: HTMLOptionsCollection
  ): string | undefined => {
    const selectedIndex = options.selectedIndex;
    return options[selectedIndex]?.getAttribute('data-id') || undefined;
  };

  const onChangeClient = (clientId?: string | number) => {
    if (clientId === undefined) return;

    const { selectedMarketplace } = state;

    const nextState = { ...state, selectedClient: clientId };

    const project =
      projects.find(
        ({ client, id, marketplace: { marketplace_type } }) =>
          client === +clientId &&
          marketplace_type === selectedMarketplace &&
          keywordGroups
            .filter(({ keywords }) => keywords.length)
            .some(({ project }) => project === id)
      ) ??
      projects.find(
        ({ client, marketplace: { marketplace_type } }) =>
          client === +clientId && marketplace_type === selectedMarketplace
      );
    if (project === undefined) {
      return clearState(nextState);
    }
    onChangeProject(project.id, nextState);
  };

  const onChangeMarketplace = (marketplace: Marketplace) => {
    const { selectedClient: prevSelectedClient } = state;

    const clientsForActualMarketplace = clients.filter(({ id }) => {
      if (
        projects.find(
          ({ client, marketplace: { marketplace_type } }) =>
            marketplace_type === marketplace && +client === +id
        )
      ) {
        return true;
      }
      return false;
    });

    const selectedClient = clientsForActualMarketplace.some(
      ({ id }) => id === prevSelectedClient
    )
      ? prevSelectedClient
      : clientsForActualMarketplace?.[0]?.id;

    const nextState = {
      ...state,
      selectedMarketplace: marketplace,
      selectedClient,
      isBrandshelf:
        marketplace !== Marketplace.YandexMarket ? false : state.isBrandshelf,
    };

    if (selectedClient === undefined) return clearState(nextState);

    const project =
      projects.find(
        ({ client, id, marketplace: { marketplace_type } }) =>
          client === +selectedClient &&
          marketplace_type === marketplace &&
          keywordGroups
            .filter(({ keywords }) => keywords.length)
            .some(({ project }) => project === id)
      ) ??
      projects.find(
        ({ client, marketplace: { marketplace_type } }) =>
          client === +selectedClient && marketplace_type === marketplace
      );

    if (project === undefined) {
      return clearState(nextState);
    }

    onChangeProject(project.id, nextState);
  };

  const onChangeProject = (
    projectId: string | number | undefined,
    nextState: Partial<IFilterState> = {}
  ) => {
    setSelectedProject?.(projectId);
    if (projectId === undefined) return;

    const findProjectCallback = ({ project }: { project: number }) =>
      project === +projectId;

    const firstProjectKeywordGroup = keywordGroups.find(findProjectCallback);
    const firstProjectCategoryGroup = categoryGroups.find(findProjectCallback);

    if (!firstProjectKeywordGroup && !firstProjectCategoryGroup) {
      setState((prev) => ({
        ...prev,
        ...nextState,
        selectedProject: projectId,
        selectedKeywordsAndCategoryGroups: [],
        selectedKeywordsAndCategories: [],
      }));
      return;
    }

    if (firstProjectKeywordGroup) {
      setState((prev) => ({
        ...prev,
        ...nextState,
        selectedProject: projectId,
        selectedKeywordsAndCategoryGroups: [
          `kg-${firstProjectKeywordGroup.id}`,
        ],
        selectedKeywordsAndCategories: firstProjectKeywordGroup.keywords.map(
          ({ keyword }) => `k-${keyword}`
        ),
      }));
    } else if (firstProjectCategoryGroup) {
      setState((prev) => ({
        ...prev,
        ...nextState,
        selectedProject: projectId,
        selectedKeywordsAndCategories:
          firstProjectCategoryGroup?.categories.map(
            ({ category }) => `c-${category}`
          ),
        selectedKeywordsAndCategoryGroups: [
          `cg-${firstProjectCategoryGroup.id}`,
        ],
      }));
    }
  };

  useEffect(() => {
    if (!state.selectedKeywordsAndCategories?.length || !dateRange)
      return setFilters(null);

    const {
      selectedKeywordsAndCategories,
      sellerType,
      selectedMarketplace,
      isBrandshelf,
      isAds,
    } = state;
    const [start_date, end_date] = dateRange.split(' - ');
    if (!start_date || !end_date) return setFilters(null);

    const getRegionsForProject = (projectId: number | string | undefined) => {
      if (projectId === undefined || isNaN(+projectId)) return ['Москва'];

      const region = projects.find(({ id }) => projectId == id)?.marketplace
        ?.region;

      if (!region) return ['Москва'];

      return Array.isArray(region) ? region : [region];
    };

    const calcType = (): FiltersTypeEnum | undefined => {
      if (currentPage === CurrentPageEnum.Dashboard && isBrandshelf)
        return FiltersTypeEnum.Brandshelf;

      if (currentPage === CurrentPageEnum.ReportGoods && isAds)
        return FiltersTypeEnum.Ads;

      return undefined;
    };

    setFilters({
      end_date,
      start_date,
      '1P': sellerType === SellerTypeEnum.OneSeller ? true : false,
      keywords: selectedKeywordsAndCategories.map(
        (keywordOrCategory) =>
          keywordOrCategory.replace(
            keywordOrCategory?.[0] === 'k' ? 'k-' : 'c-',
            ''
          ) as string
      ),
      platform: selectedMarketplace,
      type: calcType(),
      regions: getRegionsForProject(state.selectedProject),
    });
  }, [state, dateRange, setFilters]);

  useLayoutEffect(() => {
    if (
      categoryGroupsStatus !== StatusEnum.succeeded ||
      keywordGroupsStatus !== StatusEnum.succeeded ||
      projectsStatus !== StatusEnum.succeeded
    )
      return;

    if (!projects.length || !keywordGroups.length) return;

    const queryProject = JSON.parse(
      searchParams.get('selectedProject') as string
    );

    if (
      projects.find(({ id }) => id == queryProject) ||
      (!!searchParams.size && !queryProject)
    ) {
      return;
    }

    const keywordGroup = keywordGroups.find(({ keywords }) => keywords.length);
    const categoryGroup = categoryGroups.find(
      ({ categories }) => categories.length
    );
    if (!keywordGroup && !categoryGroup) return;

    if (keywordGroup) {
      const { keywords, id, project } = keywordGroup;
      const {
        client,
        marketplace: { marketplace_type },
      } = projects.find(({ id }) => project === id) as IProject;

      setState((prev) => ({
        ...prev,
        selectedKeywordsAndCategoryGroups: [`kg-${id}`],
        selectedKeywordsAndCategories: keywords.map(
          ({ keyword }) => `k-${keyword}`
        ),
        selectedProject: project,
        selectedMarketplace: marketplace_type,
        selectedClient: client,
      }));
      setSelectedProject?.(project);
    } else if (categoryGroup) {
      const { categories, id, project } = categoryGroup;
      const {
        client,
        marketplace: { marketplace_type },
      } = projects.find(({ id }) => project === id) as IProject;

      setState((prev) => ({
        ...prev,
        selectedKeywordsAndCategories: categories.map(
          ({ category }) => `c-${category}`
        ),
        selectedKeywordsAndCategoryGroups: [`cg-${id}`],
        selectedProject: project,
        selectedMarketplace: marketplace_type,
        selectedClient: client,
      }));
      setSelectedProject?.(project);
    }
  }, [categoryGroupsStatus, keywordGroupsStatus, projectsStatus]);

  const optionsActualKeywordAndCategoryGroups = [
    {
      label: 'Группы ключевых фраз',
      options: actualKeywordAndCategoryGroups
        ?.filter?.((keywordGroupWithId) => keywordGroupWithId?.includes?.('kg'))
        .map((keywordGroupWithId) => {
          const keywordId = +(keywordGroupWithId.split('-')[1] as string);
          const keyword = keywordGroups.find(({ id }) => id === keywordId);

          return {
            label: keyword?.name,
            value: keywordGroupWithId,
          };
        }),
    },
    {
      label: 'Группы категорий фраз',
      options: actualKeywordAndCategoryGroups
        ?.filter?.((categoryGroupWithId) =>
          categoryGroupWithId?.includes?.('cg')
        )
        .map((categoryGroupWithId) => {
          const categoryId = +(categoryGroupWithId.split('-')[1] as string);
          const category = categoryGroups.find(({ id }) => id === categoryId);

          return {
            label: category?.name,
            value: categoryGroupWithId,
          };
        }),
    },
  ];

  const optionsActualKeywordsAndCategories = [
    {
      label: 'Ключевые слова',
      options: actualKeywordsAndCategories
        ?.filter((keywordOrCategory) => keywordOrCategory?.[0] === 'k')
        .map((keyword) => ({
          label: keyword.replace('k-', ''),
          value: keyword,
        })),
    },
    {
      label: 'Категории',
      options: actualKeywordsAndCategories
        ?.filter((keywordOrCategory) => keywordOrCategory?.[0] === 'c')
        .map((category) => ({
          label: category.replace('c-', ''),
          value: category,
        })),
    },
  ];

  const getDateRangeLimit = (
    dateRange: string,
    index: 0 | 1
  ): Date | undefined => {
    const dateString = dateRange.split(' - ')[index];
    if (dateString) {
      const date = new Date(dateString);
      if (date && date.toString() !== 'Invalid Date') return date;
    }

    return undefined;
  };

  const onChangeKeywordAndCategoryGroup = (
    keywordsAndCategoryGroups: string[]
  ) => {
    const prevKeywordsAndCategoryGroups = [
      ...state.selectedKeywordsAndCategoryGroups,
    ];

    let newKeywordsAndCategories: string[] = [];

    if (
      keywordsAndCategoryGroups.length > prevKeywordsAndCategoryGroups.length
    ) {
      const onlyNewKeywordsAndCategoryGroups =
        keywordsAndCategoryGroups.filter?.(
          (keywordAndCategoryGroup) =>
            !prevKeywordsAndCategoryGroups?.includes?.(keywordAndCategoryGroup)
        );

      newKeywordsAndCategories = calculateActualKeywordsAndCategories(
        onlyNewKeywordsAndCategoryGroups,
        keywordGroups,
        categoryGroups
      );
    }

    return setState((prev) => {
      const newActualKeywordsAndCategories =
        calculateActualKeywordsAndCategories(
          keywordsAndCategoryGroups,
          keywordGroups,
          categoryGroups
        );

      return {
        ...prev,
        selectedKeywordsAndCategoryGroups: keywordsAndCategoryGroups,
        selectedKeywordsAndCategories: [
          ...prev.selectedKeywordsAndCategories,
          ...newKeywordsAndCategories,
        ].filter((keywordAndCategory) =>
          newActualKeywordsAndCategories?.includes?.(keywordAndCategory)
        ),
      };
    });
  };

  return (
    <div className="flex w-full flex-col gap-3 dark:text-white">
      <div className="flex items-center gap-3">
        <div className="flex w-1/2 items-center gap-3">
          <MarketplaceDropdown
            onlyForActiveOrganization
            className="shrink-0"
            selectedMarketplace={state.selectedMarketplace}
            onChangeMarketplace={onChangeMarketplace}
          />
          <Select
            title="Клиент"
            value={state.selectedClient}
            className="w-full"
            id="client-select"
            required
            disabled={!actualClients.length}
            onChange={(e) =>
              onChangeClient(getDataIdSelectedOption(e.target.options))
            }
          >
            {actualClients &&
              actualClients.map(({ name, id }) => (
                <option value={id} data-id={id} key={id}>
                  {name}
                </option>
              ))}
            {!actualClients.length && <option>Клиент</option>}
          </Select>
        </div>
        <Select
          title="Проект"
          className="w-1/2"
          value={state.selectedProject}
          id="project-select"
          required
          disabled={!actualProjects.length}
          onChange={(e) =>
            onChangeProject(getDataIdSelectedOption(e.target.options))
          }
        >
          {actualProjects.length &&
            actualProjects.map(({ name, id }) => (
              <option value={id} data-id={id} key={id}>
                {name}
              </option>
            ))}
          {!actualProjects.length && <option>Проект</option>}
        </Select>
      </div>
      {/* <Select
        className="w-full"
        id="project-select"
        value={selectedKeywordGroup as string | number}
        required
        disabled={!actualKeywordGroups.length}
        onChange={(e) =>
          onChangeKeywordGroup(getDataIdSelectedOption(e.target.options))
        }
      >
        {actualKeywordGroups.length &&
          actualKeywordGroups.map(({ name, id }) => (
            <option value={id} data-id={id} key={id}>
              {name}
            </option>
          ))}
        {!actualKeywordGroups.length && <option>Группы фраз</option>}
      </Select> */}

      <div className="flex items-center gap-3">
        <MultiSelect
          title="Группы ключевых фраз и категорий"
          mode="multiple"
          disabled={!actualKeywordAndCategoryGroups.length}
          allowClear
          className="w-1/2"
          style={{ width: '100%' }}
          placeholder="Please select"
          maxTagCount="responsive"
          showSearch
          optionFilterProp="label"
          filterOption={(input, option) =>
            (option?.label ?? '')
              .toLowerCase?.()
              ?.includes?.(input?.toLowerCase?.())
          }
          value={state.selectedKeywordsAndCategoryGroups.filter(
            (keywordAndCategoryGroup) =>
              actualKeywordAndCategoryGroups?.includes?.(
                keywordAndCategoryGroup
              )
          )}
          onChange={onChangeKeywordAndCategoryGroup}
          options={optionsActualKeywordAndCategoryGroups}
        />

        <MultiSelect
          title="Ключевые слова и категории"
          data-testid="keywordsAndCategoriesMultiSelect"
          mode="multiple"
          disabled={!actualKeywordsAndCategories?.length}
          allowClear
          className="w-1/2"
          style={{ width: '100%' }}
          placeholder="Please select"
          showSearch
          optionFilterProp="label"
          filterOption={(input, option) =>
            (option?.label ?? '')
              ?.toLowerCase?.()
              ?.includes?.(input?.toLowerCase?.())
          }
          value={state.selectedKeywordsAndCategories.filter(
            (keywordAndCategory) =>
              actualKeywordsAndCategories?.includes?.(keywordAndCategory)
          )}
          maxTagCount="responsive"
          onChange={(keywordsAndCategories) =>
            setState((prev) => ({
              ...prev,
              selectedKeywordsAndCategories: keywordsAndCategories.filter(
                (keywordAndCategory) =>
                  actualKeywordsAndCategories?.includes?.(keywordAndCategory)
              ),
            }))
          }
          options={optionsActualKeywordsAndCategories}
        />
      </div>

      {/* <Select
        className="w-full"
        id="project-select"
        disabled={!actualKeywords?.length}
        value={selectedKeyword as string | number}
        required
        onChange={(e) => setSelectedKeyword(e.target.value)}
      >
        {actualKeywords?.length &&
          actualKeywords.map(({ keyword }, index) => (
            <option value={keyword} key={index}>
              {keyword}
            </option>
          ))}
        {!actualKeywords?.length && <option>Фразы</option>}
      </Select> */}
      <div className="flex items-center gap-3">
        <DateRange
          defaultRangeStart={getDateRangeLimit(dateRange, 0)}
          defaultRangeEnd={getDateRangeLimit(dateRange, 1)}
          className="w-full max-w-lg"
          placeholder={'Select Period'}
          setParentValue={setDateRange}
        />
        <div title="Тип продавца" className="flex items-center">
          <div className="w-48">
            <Dropdown
              label="Тип продавца"
              dismissOnClick={true}
              renderTrigger={() => {
                return (
                  <button className="flex w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <span>
                      {state.sellerType === SellerTypeEnum.All && 'Все'}
                      {state.sellerType === SellerTypeEnum.OneSeller &&
                        '1p продавец'}
                      {state.sellerType === SellerTypeEnum.ThirdPartySeller &&
                        '3p продавец'}
                    </span>
                    <svg
                      className="ml-2 h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                );
              }}
            >
              <Dropdown.Item
                onClick={() =>
                  setState((prev) => ({
                    ...prev,
                    sellerType: SellerTypeEnum.All,
                  }))
                }
              >
                Все
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() =>
                  setState((prev) => ({
                    ...prev,
                    sellerType: SellerTypeEnum.OneSeller,
                  }))
                }
              >
                1p продавец (продавец {state.selectedMarketplace})
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => {}}
                disabled
                className="cursor-not-allowed opacity-50"
              >
                3p продавец
              </Dropdown.Item>
            </Dropdown>
          </div>
        </div>
        {setIsMyBrands && isMyBrands !== undefined && (
          <div title="Мои бренды" className="flex items-center">
            <div>
              <ToggleSwitch checked={isMyBrands} onChange={setIsMyBrands} />
            </div>
            <span className="ml-2">
              <div className="text-base">Мои бренды</div>
              <div className="text-sm text-gray-500">Только нужное</div>
            </span>
          </div>
        )}
        {currentPage === CurrentPageEnum.Dashboard &&
          state.selectedMarketplace === Marketplace.YandexMarket && (
            <div className="flex items-center">
              <div>
                <ToggleSwitch
                  checked={!!state?.isBrandshelf}
                  onChange={() =>
                    setState((prev) => ({
                      ...prev,
                      isBrandshelf: !prev.isBrandshelf,
                    }))
                  }
                />
              </div>
              <span className="ml-2">
                <div className="text-base">Brandshelf</div>
                <div className="text-sm text-gray-500">Только полка</div>
              </span>
            </div>
          )}
        {currentPage === CurrentPageEnum.ReportGoods &&
          marketplacesWithAds?.includes?.(state.selectedMarketplace) && (
            <div title="Ads only" className="flex items-center">
              <div>
                <ToggleSwitch
                  checked={!!state?.isAds}
                  onChange={() =>
                    setState((prev) => ({
                      ...prev,
                      isAds: !prev.isAds,
                    }))
                  }
                />
              </div>
              <span className="ml-2">
                <div className="text-base">Ads only</div>
                {/* <div className="text-sm text-gray-500">Только полка</div> */}
              </span>
            </div>
          )}
      </div>
      {/* <div>
        <div>
          <Radio
            defaultChecked
            id="desktop-check"
            name="sources"
            value="Desktop"
          />
          <Label className="ml-2 text-sm" htmlFor="desktop-check">
            Desktop
          </Label>
        </div>
        <div>
          <Radio defaultChecked id="app-check" name="sources" value="App" />
          <Label className="ml-2 text-sm" htmlFor="app-check">
            App
          </Label>
        </div>
      </div> */}
      {/* <div className="ml-auto">
        <label htmlFor="brand" className="sr-only">
          Сравнить бренды
        </label>
        <select
          id={`marketplace-for-client`}
          onChange={() => console.log('onChange')}
          className="peer block w-full appearance-none border-0 border-b-2 border-gray-200 bg-transparent py-2.5 px-0 text-sm text-gray-500 focus:border-gray-200 focus:outline-none focus:ring-0 dark:border-gray-700 dark:text-gray-400"
        >
          <option value={''}>All marketplaces</option>

          <option>sss</option>
        </select>
      </div> */}
      {showBrands && (
        <div className="grow">
          <BrandsColorEditor />
        </div>
      )}
    </div>
  );
};

export default memo(Filters);
